import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import "./JsonTreeView.scss";

function JsonPrimitive({ value }) {
  if (value === null) return <span className="json-tree-view__value--null">null</span>;
  switch (typeof value) {
    case "string":
      return <span className="json-tree-view__value--string">&quot;{value}&quot;</span>;
    case "number":
      return <span className="json-tree-view__value--number">{value}</span>;
    case "boolean":
      return <span className="json-tree-view__value--boolean">{String(value)}</span>;
    default:
      return <span>{String(value)}</span>;
  }
}

function getTypeLabel(value) {
  if (Array.isArray(value)) return `array(${value.length})`;
  if (value === null) return "null";
  if (typeof value === "object") return `object(${Object.keys(value).length})`;
  return typeof value;
}

function JsonPrimitiveNode({ label, value }) {
  return (
    <div className="json-tree-view__node">
      {label !== null && <span className="json-tree-view__key">{label}: </span>}
      <JsonPrimitive value={value} />
    </div>
  );
}

function JsonExpandableNode({ label, value, depth, initiallyExpandedDepth, expandMode, expandSignal, path }) {
  const entries = useMemo(
    () => (Array.isArray(value) ? value.map((v, i) => [String(i), v]) : Object.entries(value)),
    [value]
  );

  const openByDefault = depth < initiallyExpandedDepth;
  const typeLabel = getTypeLabel(value);

  const [isOpen, setIsOpen] = useState(() => {
    if (expandMode === "all") return true;
    if (expandMode === "none") return false;
    return openByDefault;
  });

  useEffect(() => {
    if (expandMode === "all") setIsOpen(true);
    else if (expandMode === "none") setIsOpen(false);
    else setIsOpen(openByDefault);
  }, [expandSignal, expandMode, openByDefault]);

  return (
    <div className="json-tree-view__node">
      <details
        open={isOpen}
        onToggle={(e) => {
          setIsOpen(e.currentTarget.open);
        }}
      >
        <summary className="json-tree-view__summary">
          {label !== null && <span className="json-tree-view__key">{label}</span>}
          <span className="json-tree-view__type">{typeLabel}</span>
        </summary>
        {entries.length === 0 && <div className="json-tree-view__node">{Array.isArray(value) ? "[]" : "{}"}</div>}
        {entries.map(([k, v]) => (
          <JsonNode
            key={`${path}.${k}`}
            label={Array.isArray(value) ? `[${k}]` : k}
            value={v}
            depth={depth + 1}
            initiallyExpandedDepth={initiallyExpandedDepth}
            expandMode={expandMode}
            expandSignal={expandSignal}
            path={`${path}.${k}`}
          />
        ))}
      </details>
    </div>
  );
}

function JsonNode({ label, value, depth, initiallyExpandedDepth, expandMode, expandSignal, path }) {
  const isExpandable = value !== null && typeof value === "object";
  if (!isExpandable) return <JsonPrimitiveNode label={label} value={value} />;
  return (
    <JsonExpandableNode
      label={label}
      value={value}
      depth={depth}
      initiallyExpandedDepth={initiallyExpandedDepth}
      expandMode={expandMode}
      expandSignal={expandSignal}
      path={path}
    />
  );
}

function JsonTreeView({ data, initiallyExpandedDepth = 2, expandMode = "default", expandSignal = 0 }) {
  return (
    <div className="json-tree-view">
      <JsonNode
        label={null}
        value={data}
        depth={0}
        initiallyExpandedDepth={initiallyExpandedDepth}
        expandMode={expandMode}
        expandSignal={expandSignal}
        path="$"
      />
    </div>
  );
}

JsonTreeView.propTypes = {
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.array, PropTypes.object]),
  initiallyExpandedDepth: PropTypes.number,
  expandMode: PropTypes.oneOf(["default", "all", "none"]),
  expandSignal: PropTypes.number,
};

export default JsonTreeView;
